import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './AgriProducts.css';

function FadeSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('visible'); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`fade-in ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

const products = [
  {
    icon: '🌿',
    name: 'Organic Fertilizers',
    category: 'Soil Health',
    desc: 'Bio-based fertilizers enriched with natural micronutrients, boosting soil health and crop yield without harmful chemicals.',
    badge: 'Best Seller',
    badgeColor: '#1a5c2e',
  },
  {
    icon: '🌱',
    name: 'Native Saplings',
    category: 'Tree Plantation',
    desc: 'Forest Department certified native species saplings — selected for ecological compatibility, climate resilience, and biodiversity.',
    badge: 'Forest Certified',
    badgeColor: '#2d8a4e',
  },
  {
    icon: '🍃',
    name: 'Herbal Crop Seeds',
    category: 'Sustainable Farming',
    desc: 'Curated heirloom and open-pollinated seeds for medicinal herbs, native vegetables, and climate-resilient crops.',
    badge: 'New Arrival',
    badgeColor: '#c9a227',
  },
  {
    icon: '🌾',
    name: 'Natural Pest Control',
    category: 'Crop Protection',
    desc: 'Chemical-free, bio-pest control solutions derived from neem, marigold, and other plant-based compounds.',
    badge: 'Eco-Safe',
    badgeColor: '#4a9eda',
  },
  {
    icon: '💧',
    name: 'Water Conservation Kits',
    category: 'Irrigation',
    desc: 'Drip irrigation starter kits and rainwater harvesting tools for small and marginal farmers, reducing water usage by 60%.',
    badge: 'Govt. Subsidized',
    badgeColor: '#9c27b0',
  },
  {
    icon: '🌻',
    name: 'Agri Training Programs',
    category: 'Education',
    desc: 'Hands-on training in sustainable farming, composting, organic certification, and market linkage for rural farmers.',
    badge: 'Free Program',
    badgeColor: '#ff6b2b',
  },
];

const values = [
  { icon: '🌍', title: 'Eco-Conscious', desc: 'Every product is designed to reduce environmental impact and promote biodiversity.' },
  { icon: '👨‍🌾', title: 'Farmer First', desc: 'Direct partnerships with smallholder farmers — fair prices, better livelihoods.' },
  { icon: '🧪', title: 'Science-Backed', desc: 'Products developed in consultation with agricultural scientists and Forest Departments.' },
  { icon: '📦', title: 'Market Linkage', desc: 'We connect sustainable agri-products directly to consumers and export markets.' },
];

export default function AgriProducts() {
  return (
    <div className="agri-page">
      {/* HERO */}
      <section className="page-hero agri-hero">
        <div className="agri-hero__bg" />
        <div className="page-hero__overlay" />
        <div className="container page-hero__content">
          <span className="tag" style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--gold-light)' }}>
            Agri Startup
          </span>
          <h1 className="page-hero__title">
            From Soil to Soul —<br />
            <span style={{ color: 'var(--gold-light)' }}>Sustainable Agriculture</span>
          </h1>
          <p className="page-hero__sub">
            Y2K Global Nexus's agri-products startup empowers farmers with eco-friendly solutions,
            connecting nature-first agriculture to markets and building a sustainable food future.
          </p>
        </div>
      </section>

      {/* INTRO */}
      <section className="agri-intro section-padding">
        <div className="container">
          <div className="agri-intro__grid">
            <FadeSection>
              <div>
                <span className="tag">Our Agri Mission</span>
                <h2 className="section-title">Empowering Farmers,<br />Healing the Earth</h2>
                <p className="section-subtitle" style={{ marginBottom: '24px' }}>
                  Agriculture and environment are inseparable. Y2K's agri-startup arm bridges the gap
                  between modern sustainable farming practices and India's rural farming communities.
                </p>
                <p style={{ color: 'var(--text-medium)', lineHeight: '1.8', marginBottom: '32px' }}>
                  We believe in a future where every farm is a forest — where chemical-free cultivation
                  produces healthy food, healthy soil, and healthy communities. Our products and programs
                  make sustainable farming economically viable for every farmer.
                </p>
                <div className="agri-intro__highlights">
                  <div className="agri-stat-pill">🌾 50+ Agri Products</div>
                  <div className="agri-stat-pill">👨‍🌾 1,000+ Farmers Supported</div>
                  <div className="agri-stat-pill">🌍 Climate-Smart Farming</div>
                  <div className="agri-stat-pill">🏆 Forest Dept. Certified</div>
                </div>
              </div>
            </FadeSection>
            <FadeSection delay={200}>
              <div className="agri-intro__visual">
                <div className="agri-visual-card">
                  <div className="agri-visual-card__bg" />
                  <div className="agri-visual-card__content">
                    <div className="agri-visual-card__icon">🌾</div>
                    <h3>Y2K Agri Startup</h3>
                    <p>Nature-first agriculture for a sustainable tomorrow</p>
                    <div className="agri-visual-card__tags">
                      <span>Organic</span>
                      <span>Sustainable</span>
                      <span>Eco-Friendly</span>
                      <span>Market-Linked</span>
                    </div>
                  </div>
                </div>
              </div>
            </FadeSection>
          </div>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="agri-products section-padding" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <FadeSection>
            <div style={{ textAlign: 'center', marginBottom: '56px' }}>
              <span className="tag">Products & Programs</span>
              <h2 className="section-title">Our Agri Portfolio</h2>
              <p className="section-subtitle" style={{ margin: '0 auto' }}>
                A comprehensive range of sustainable agri-products and programs for modern, eco-conscious farming.
              </p>
            </div>
          </FadeSection>
          <div className="agri-products__grid">
            {products.map(({ icon, name, category, desc, badge, badgeColor }, i) => (
              <FadeSection key={i} delay={i * 80}>
                <div className="agri-product-card">
                  {badge && (
                    <div className="agri-product-card__badge" style={{ background: badgeColor }}>
                      {badge}
                    </div>
                  )}
                  <div className="agri-product-card__icon">{icon}</div>
                  <div className="agri-product-card__category">{category}</div>
                  <h3 className="agri-product-card__name">{name}</h3>
                  <p className="agri-product-card__desc">{desc}</p>
                  <button className="agri-product-card__btn">Learn More</button>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="agri-values section-padding">
        <div className="container">
          <FadeSection>
            <div style={{ textAlign: 'center', marginBottom: '56px' }}>
              <span className="tag">Why Choose Us</span>
              <h2 className="section-title">Our Agri Principles</h2>
            </div>
          </FadeSection>
          <div className="agri-values__grid">
            {values.map(({ icon, title, desc }, i) => (
              <FadeSection key={i} delay={i * 100}>
                <div className="agri-value-card">
                  <div className="agri-value-card__icon">{icon}</div>
                  <h3>{title}</h3>
                  <p>{desc}</p>
                </div>
              </FadeSection>
            ))}
          </div>
        </div>
      </section>

      {/* TREE + AGRI SYNERGY */}
      <section className="agri-synergy section-padding" style={{ background: 'var(--cream)' }}>
        <div className="container">
          <FadeSection>
            <div className="agri-synergy__card">
              <div className="agri-synergy__content">
                <span className="tag">The Connection</span>
                <h2 className="section-title">Agri + Forest = Future</h2>
                <p className="section-subtitle" style={{ marginBottom: '24px' }}>
                  Our agri startup and tree-planting initiatives are deeply interlinked.
                  Trees protect farmland from erosion, regulate temperature, improve water retention,
                  and create microhabitats that boost agricultural productivity.
                </p>
                <p style={{ color: 'var(--text-medium)', lineHeight: '1.8' }}>
                  Through Y2K Global Nexus, every tree planted supports local agriculture,
                  and every farm product sold funds more plantations. This circular model
                  is what makes our work truly sustainable — and what we are scaling globally.
                </p>
              </div>
              <div className="agri-synergy__visual">
                <div className="agri-synergy-circle">
                  <div className="agri-synergy-center">♻️</div>
                  <div className="agri-synergy-item agri-synergy-item--1">🌳<span>Trees</span></div>
                  <div className="agri-synergy-item agri-synergy-item--2">🌾<span>Farming</span></div>
                  <div className="agri-synergy-item agri-synergy-item--3">🌍<span>Planet</span></div>
                  <div className="agri-synergy-item agri-synergy-item--4">👨‍🌾<span>People</span></div>
                </div>
              </div>
            </div>
          </FadeSection>
        </div>
      </section>

      {/* CTA */}
      <section className="agri-cta">
        <div className="container">
          <FadeSection>
            <div className="agri-cta__inner">
              <h2>Partner With Our <span className="gold-text">Agri Startup</span></h2>
              <p>
                Farmer, investor, retailer, or NGO partner — let's build a sustainable
                agricultural ecosystem together. Connect with Y2K Global Nexus today.
              </p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Link to="/contact" className="btn-gold">🌾 Partner With Us</Link>
                <Link to="/about" className="btn-outline" style={{ borderColor: 'white', color: 'white' }}>
                  Learn About Y2K
                </Link>
              </div>
            </div>
          </FadeSection>
        </div>
      </section>
    </div>
  );
}
